import { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, ArrowUp, Bot, X, Send, Loader2, Mail } from "lucide-react";
import { CONTACT } from "../data/site";
import { api } from "../lib/api";

function sessionId() {
  let id = sessionStorage.getItem("sourcex-chat-session");
  if (!id) {
    id = "sess-" + Math.random().toString(36).slice(2);
    sessionStorage.setItem("sourcex-chat-session", id);
  }
  return id;
}

export default function FloatingWidgets() {
  const [showTop, setShowTop] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "bot", text: "Hi! I'm the SourceX sourcing assistant. Ask me about commodities, industries, RFQs or getting a quote." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  const wa = `https://wa.me/${CONTACT.phoneRaw}?text=${encodeURIComponent("Hi SourceX Global, I'd like to discuss a sourcing requirement.")}`;

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 500);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, chatOpen]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { message: text, session_id: sessionId() });
      setMessages((m) => [...m, { role: "bot", text: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "bot", text: "Sorry, I'm unavailable right now. Please email info@sourcex-global.com or WhatsApp +91 74200 64630." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Chat panel */}
      <AnimatePresence>
        {chatOpen && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-24 right-5 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[520px] bg-card border border-border shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="bg-navy text-white px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="h-8 w-8 bg-brand-orange grid place-items-center"><Bot size={18} /></span>
                <div>
                  <div className="font-display font-bold text-sm">SourceX Assistant</div>
                  <div className="text-[10px] text-white/50 font-mono uppercase tracking-[0.15em]">AI Sourcing Concierge</div>
                </div>
              </div>
              <button onClick={() => setChatOpen(false)} data-testid="chat-close" className="text-white/70 hover:text-white"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-secondary">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user" ? "bg-brand-orange text-white" : "bg-card border border-border text-foreground"
                  }`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-card border border-border px-3.5 py-2.5"><Loader2 size={16} className="animate-spin text-brand-orange" /></div>
                </div>
              )}
              <div ref={endRef} />
            </div>
            <form onSubmit={send} className="p-3 border-t border-border flex gap-2 bg-card">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about sourcing..."
                className="flex-1 bg-secondary border border-border px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground rounded-none focus:outline-none focus:border-brand-orange"
              />
              <button data-testid="chat-send" type="submit" disabled={loading}
                className="bg-brand-orange text-white px-3.5 grid place-items-center hover:bg-brand-orangeHover transition-colors disabled:opacity-60">
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Right stack */}
      <div className="fixed bottom-6 right-5 z-40 flex flex-col items-center gap-3">
        <AnimatePresence>
          {showTop && (
            <motion.button
              initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.6 }}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              data-testid="back-to-top"
              className="h-11 w-11 bg-card border border-border text-foreground grid place-items-center hover:border-brand-orange hover:text-brand-orange transition-colors shadow-lg"
              aria-label="Back to top"
            >
              <ArrowUp size={18} />
            </motion.button>
          )}
        </AnimatePresence>
        <a href={`mailto:${CONTACT.email}`} data-testid="float-email"
          className="h-12 w-12 bg-navy grid place-items-center shadow-lg hover:bg-navy-soft transition-colors" aria-label="Email us">
          <Mail size={20} className="text-white" />
        </a>
        <a href={wa} target="_blank" rel="noreferrer" data-testid="whatsapp-float"
          className="h-12 w-12 bg-[#25D366] grid place-items-center shadow-lg hover:scale-105 transition-transform relative" aria-label="WhatsApp">
          <MessageCircle size={22} className="text-white" fill="white" />
          <span className="absolute inset-0 animate-ping bg-[#25D366]/40" />
        </a>
        <button onClick={() => setChatOpen((v) => !v)} data-testid="chat-toggle"
          className="h-14 w-14 bg-brand-orange grid place-items-center shadow-xl hover:bg-brand-orangeHover transition-colors" aria-label="AI Assistant">
          {chatOpen ? <X size={24} className="text-white" /> : <Bot size={26} className="text-white" />}
        </button>
      </div>
    </>
  );
}
